import { call, put, takeLatest } from "redux-saga/effects";
import { constructUrl } from "@pl/paylican-ui-kit";
import * as actions from "./actions";
import { FETCHING_INVOICE_LIST, FETCHING_STATEMENT, FETCHING_NAME, CREATE_EDIT_STATEMENT } from "./types";
import { setRequestInProcess } from "../request/actions";
import invoicesApiService from "../../services/invoicesApiService";
import { invoiceAdaptor, invoiceGridDataAdaptor } from "../../utils/invoiceAdaptors";
import { UNPAID } from "../../constants/invoiceStatuses";
import { showModal } from "../modal/actions";
import modalNames from "../../constants/modalNames";
import { invoice as invoicePath } from "../../constants/pathnames";

function* fetchInvoiceListAsync(action) {
  try {
    yield put(setRequestInProcess(FETCHING_INVOICE_LIST, true));

    const request = {
      statuses: [UNPAID],
      includeOnlyInvoices: true,
      size: 1000,
      page: 0,
      ...action.payload
    };

    const response = yield call(x => invoicesApiService.searchInvoices(x), request);

    yield put(setRequestInProcess(FETCHING_INVOICE_LIST, false));

    if (response.hasError) {
      yield put(actions.fetchingInvoiceListError(response.errors));
      yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
    } else {
      const invoices = response.payload.content.map(i => invoiceGridDataAdaptor(i));
      yield put(actions.fetchingInvoiceListSuccess(invoices));
    }
  } catch (err) {
    yield put(actions.fetchingInvoiceListError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchFetchInvoiceListAsync() {
  yield takeLatest(FETCHING_INVOICE_LIST, fetchInvoiceListAsync);
}

function* fetchingStatementAsync(action) {
  try {
    yield put(setRequestInProcess(FETCHING_STATEMENT, true));

    const response = yield call(x => invoicesApiService.getInvoice(x), action.payload);

    yield put(setRequestInProcess(FETCHING_STATEMENT, false));

    if (response.hasError) {
      yield put(actions.fetchingStatementError(response.errors));
      yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
    } else {
      yield put(actions.fetchingStatementSuccess(invoiceAdaptor(response.payload)));
    }
  } catch (err) {
    yield put(actions.fetchingStatementError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchFetchingStatementAsync() {
  yield takeLatest(FETCHING_STATEMENT, fetchingStatementAsync);
}

function* fetchingNameAsync() {
  try {
    yield put(setRequestInProcess(FETCHING_NAME, true));

    const response = yield call(() => invoicesApiService.getName());

    yield put(setRequestInProcess(FETCHING_NAME, false));

    if (response.hasError) {
      yield put(actions.fetchingNameError(response.errors));
      yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
    } else {
      yield put(actions.fetchingNameSuccess(response.payload));
    }
  } catch (err) {
    yield put(actions.fetchingNameError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchFetchingNameAsync() {
  yield takeLatest(FETCHING_NAME, fetchingNameAsync);
}

function* createEditStatementAsync(action) {
  try {
    yield put(setRequestInProcess(CREATE_EDIT_STATEMENT, true));

    const { request, id, navigate } = action.payload;

    let response;
    if (id) {
      response = yield call((x, y) => invoicesApiService.updateStatement(x, y), request, id);
    } else {
      response = yield call(x => invoicesApiService.createStatement(x), request);
    }

    yield put(setRequestInProcess(CREATE_EDIT_STATEMENT, false));

    if (response.hasError) {
      yield put(actions.createEditStatementError(response.errors));
      yield put(
        showModal(modalNames.ERROR_MODAL, {
          errors: response.errors.map(err => {
            if (err.id === "status_invalid") {
              return {
                id: `invoice_invalid`,
                data: {
                  data: id
                }
              };
            }
            return err;
          })
        })
      );
    } else {
      yield put(actions.createEditStatementSuccess());

      const statementId = id ?? response.payload.code;

      navigate(constructUrl(invoicePath, { id: statementId }), { replace: true });
    }
  } catch (err) {
    yield put(actions.createEditStatementError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchCreateEditStatementAsync() {
  yield takeLatest(CREATE_EDIT_STATEMENT, createEditStatementAsync);
}

export {
  watchFetchInvoiceListAsync,
  watchFetchingStatementAsync,
  watchFetchingNameAsync,
  watchCreateEditStatementAsync
};
